import React, { FC } from 'react';
import { fmtMinutes, fmtPct } from '../scaling/scalingCard';
import { BaselineNote, ImpactChart, colours } from './ImpactChart';
import { ImpactTile } from './ImpactTile';
import { fmtCount } from './outcomeSections';
import { currentWeek, headlineWeeks, weekLabel } from './series';
import type { Granularity } from './series';
import type {
  CaptchaBucket,
  CaptchaPayload,
  SecureInputBucket,
  SecureInputPayload,
} from './types';

/**
 * Sections 4–5 of the Browser Ladder tab: CAPTCHA autonomy and Secure Input
 * (machine-to-machine login). Both answer "how much of this work no longer
 * reaches a human".
 */

const captchaFooter = (b: CaptchaBucket) =>
  `machine ${b.machineSolved}/${b.machineAttempts} · human tasks ${b.humanCaptchaTasks} · escalations ${b.escalations}`;

/**
 * Monday of the week `browser_captcha_solve_events` started being written
 * (dream-be #1808). Complete weeks before this necessarily show 0 machine
 * solves, which says nothing about how many CAPTCHAs humans really solved.
 */
export const MACHINE_CAPTCHA_TELEMETRY_START = '2026-09-14';

/** Headline weeks that predate the solver telemetry get an explanatory note. */
export function headlinePredatesTelemetry(week?: CaptchaBucket): boolean {
  return (
    week != null &&
    week.bucket < MACHINE_CAPTCHA_TELEMETRY_START &&
    week.machineAttempts === 0
  );
}

export const CaptchaAutonomy: FC<{
  data: CaptchaPayload;
  granularity: Granularity;
}> = ({ data, granularity }) => {
  const buckets = data[granularity];
  const { week, prior } = headlineWeeks(data.weekly);
  const base = data.baseline.value;
  const definition = data.definitions.captcha ?? 'No definition available.';
  const work = (b?: CaptchaBucket) =>
    b ? b.machineSolved + b.humanCaptchaTasks : undefined;
  const thisWeek = currentWeek(data.weekly);
  const soFar = (
    value: (b: CaptchaBucket) => number | null,
    sampleN?: number,
  ) =>
    thisWeek
      ? { weekBucket: thisWeek.bucket, value: value(thisWeek), sampleN }
      : undefined;
  return (
    <>
      {headlinePredatesTelemetry(week) && (
        <p className='ladder-baseline-note ladder-telemetry-note'>
          Machine CAPTCHA solves are only recorded from{' '}
          {weekLabel(MACHINE_CAPTCHA_TELEMETRY_START)}. Earlier weeks show 0
          machine solves because the solver telemetry did not exist yet, not
          because humans solved every CAPTCHA.
        </p>
      )}
      <div className='scaling-grid'>
        <ImpactTile
          index={1}
          title='CAPTCHAs solved by machines'
          weekBucket={week?.bucket}
          current={week?.machineSolved ?? null}
          previous={prior?.machineSolved ?? null}
          baseline={base?.machineSolved ?? null}
          sampleN={week?.machineAttempts}
          previousSampleN={prior?.machineAttempts}
          sampleUnit='attempts'
          format={fmtCount}
          lowerIsBetter={false}
          details={definition}
          soFar={soFar((b) => b.machineSolved, thisWeek?.machineAttempts)}
        />
        <ImpactTile
          index={2}
          title='CAPTCHA work done by machines'
          weekBucket={week?.bucket}
          current={week?.machineSharePct ?? null}
          previous={prior?.machineSharePct ?? null}
          baseline={base?.machineSharePct ?? null}
          sampleN={work(week)}
          previousSampleN={work(prior)}
          sampleUnit='CAPTCHAs'
          format={fmtPct}
          lowerIsBetter={false}
          suffix='%'
          details={definition}
          soFar={soFar((b) => b.machineSharePct, work(thisWeek))}
        />
        <ImpactTile
          index={3}
          title='CAPTCHA tasks reaching humans'
          weekBucket={week?.bucket}
          current={week?.humanCaptchaTasks ?? null}
          previous={prior?.humanCaptchaTasks ?? null}
          baseline={base?.humanCaptchaTasks ?? null}
          sampleN={work(week)}
          previousSampleN={work(prior)}
          sampleUnit='CAPTCHAs'
          format={fmtCount}
          lowerIsBetter
          details={definition}
          soFar={soFar((b) => b.humanCaptchaTasks, work(thisWeek))}
        />
      </div>
      <div className='scaling-grid browser-ladder-charts'>
        <ImpactChart
          title='CAPTCHAs — machine vs human'
          definition={definition}
          buckets={buckets}
          granularity={granularity}
          markers={data.releaseMarkers}
          tooltipFooter={captchaFooter}
          series={[
            {
              label: 'Solved by machine',
              value: (b) => b.machineSolved,
              colour: colours.green,
            },
            {
              label: 'Human CAPTCHA tasks',
              value: (b) => b.humanCaptchaTasks,
              colour: colours.red,
              baseline: base?.humanCaptchaTasks,
              baselineIsCount: true,
            },
            {
              label: 'Escalated to human',
              value: (b) => b.escalations,
              colour: colours.amber,
            },
          ]}
        />
        <ImpactChart
          title='CAPTCHA autonomy (%)'
          definition={definition}
          buckets={buckets}
          granularity={granularity}
          markers={data.releaseMarkers}
          sampleOf={(b) => b.machineSolved + b.humanCaptchaTasks}
          tooltipFooter={captchaFooter}
          yMax={100}
          series={[
            {
              label: 'Share of CAPTCHA work done by machines',
              value: (b) => b.machineSharePct,
              colour: colours.green,
              baseline: base?.machineSharePct,
            },
            {
              label: 'Machine solve rate',
              value: (b) => b.machineSolveRatePct,
              colour: colours.blue,
            },
          ]}
        />
      </div>
      <BaselineNote label={data.baseline.label} />
    </>
  );
};

const secureInputFooter = (b: SecureInputBucket) =>
  `n = ${b.requests} requests · authenticated ${b.signedIn} · human login tasks ${
    b.loginInterventions + b.loginWebActions
  }`;

const humanLogins = (b?: SecureInputBucket | null) =>
  b == null ? null : b.loginInterventions + b.loginWebActions;

export const SecureInputAuth: FC<{
  data: SecureInputPayload;
  granularity: Granularity;
}> = ({ data, granularity }) => {
  const buckets = data[granularity];
  const { week, prior } = headlineWeeks(data.weekly);
  const base = data.baseline.value;
  const logins = (b?: SecureInputBucket) =>
    b ? b.signedIn + (humanLogins(b) ?? 0) : undefined;
  const definition = data.definitions.secureInput ?? 'No definition available.';
  const topReasons = data.failureReasons.slice(0, 5);
  return (
    <>
      <div className='scaling-grid'>
        <ImpactTile
          index={1}
          title='Secure Input success rate'
          weekBucket={week?.bucket}
          current={week?.successRatePct ?? null}
          previous={prior?.successRatePct ?? null}
          baseline={base?.successRatePct ?? null}
          sampleN={week?.requests}
          previousSampleN={prior?.requests}
          sampleUnit='requests'
          format={fmtPct}
          lowerIsBetter={false}
          suffix='%'
          details={definition}
        />
        <ImpactTile
          index={2}
          title='Logins completed with no human'
          weekBucket={week?.bucket}
          current={week?.secureInputSharePct ?? null}
          previous={prior?.secureInputSharePct ?? null}
          baseline={base?.secureInputSharePct ?? null}
          sampleN={logins(week)}
          previousSampleN={logins(prior)}
          sampleUnit='logins'
          format={fmtPct}
          lowerIsBetter={false}
          suffix='%'
          details={definition}
        />
        <ImpactTile
          index={3}
          title='Human login tasks'
          weekBucket={week?.bucket}
          current={humanLogins(week)}
          previous={humanLogins(prior)}
          baseline={humanLogins(base)}
          sampleN={logins(week)}
          previousSampleN={logins(prior)}
          sampleUnit='logins'
          format={fmtCount}
          lowerIsBetter
          details={definition}
        />
      </div>
      <div className='scaling-grid browser-ladder-charts'>
        <ImpactChart
          title='Login demand — Secure Input vs humans'
          definition={definition}
          buckets={buckets}
          granularity={granularity}
          markers={data.releaseMarkers}
          tooltipFooter={secureInputFooter}
          series={[
            {
              label: 'Authenticated via Secure Input',
              value: (b) => b.signedIn,
              colour: colours.green,
            },
            {
              label: 'Login browser interventions',
              value: (b) => b.loginInterventions,
              colour: colours.red,
              baseline: base?.loginInterventions,
              baselineIsCount: true,
            },
            {
              label: 'Login web actions',
              value: (b) => b.loginWebActions,
              colour: colours.amber,
              baseline: base?.loginWebActions,
              baselineIsCount: true,
            },
          ]}
        />
        <ImpactChart
          title='Secure Input rates (%)'
          definition={definition}
          buckets={buckets}
          granularity={granularity}
          markers={data.releaseMarkers}
          sampleOf={(b) => b.requests}
          tooltipFooter={secureInputFooter}
          yMax={100}
          series={[
            {
              label: 'Success rate',
              value: (b) => b.successRatePct,
              colour: colours.green,
            },
            {
              label: 'Logins with no human',
              value: (b) => b.secureInputSharePct,
              colour: colours.blue,
            },
            {
              label: 'User responded',
              value: (b) => b.userResponsePct,
              colour: colours.purple,
            },
          ]}
        />
        <section className='scaling-card'>
          <header className='scaling-card-header'>
            <h3>Median minutes to authenticate</h3>
          </header>
          <div className='scaling-card-value-row'>
            <span className='scaling-value'>
              {fmtMinutes(week?.medianMinutesToAuth ?? null)}
            </span>
          </div>
          <p className='scaling-card-sub'>
            Last complete week, from request to authenticated.
          </p>
          <h4 className='ladder-list-title'>Top failure reasons (window)</h4>
          {topReasons.length === 0 ? (
            <p className='scaling-card-sub'>No failures recorded.</p>
          ) : (
            <ul className='ladder-reason-list'>
              {topReasons.map((r) => (
                <li key={r.reasonCode}>
                  <span>{r.reasonCode}</span>
                  <strong>{r.count}</strong>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
      <BaselineNote label={data.baseline.label} />
    </>
  );
};
